import React from "react";
import { Handle, Position } from "@xyflow/react";
import { FiChevronDown, FiChevronRight, FiDatabase, FiFileText, FiFolder, FiGrid, FiLayers } from "react-icons/fi";

/** Data carried by every architecture graph node. */
export type ArchNodeData = {
	label: string;
	sublabel?: string;
	kind: "root" | "folder" | "page" | "dynamic";
	recordCount?: number | null;
	childCount: number;
	hasChildren: boolean;
	expanded: boolean;
	dimmed?: boolean;
	matched?: boolean;
	onToggle?: () => void;
	onOpen?: () => void;
};

const KIND_ICON = { root: FiGrid, folder: FiFolder, page: FiFileText, dynamic: FiLayers } as const;

const KIND_STYLE: Record<ArchNodeData["kind"], { ring: string; chip: string; icon: string }> = {
	root: { ring: "border-slate-900 bg-slate-900 text-white", chip: "bg-white/15 text-white", icon: "text-white" },
	folder: { ring: "border-slate-200 bg-white text-slate-700", chip: "bg-slate-100 text-slate-500", icon: "text-slate-400" },
	page: { ring: "border-slate-200 bg-white text-slate-800", chip: "bg-slate-100 text-slate-500", icon: "text-sky-500" },
	dynamic: { ring: "border-sky-300 bg-sky-50 text-slate-900", chip: "bg-sky-100 text-sky-700", icon: "text-sky-600" },
};

/** One node in the site-architecture graph: an icon, the page/folder name, a count chip, and (for
 *  branches) an expand toggle. Edges connect a node to its parent (target=left, source=right). */
const ArchitectureNode: React.FC<{ data: ArchNodeData }> = ({ data }) => {
	const Icon = KIND_ICON[data.kind] ?? FiFileText;
	const s = KIND_STYLE[data.kind];
	return (
		<div
			className={`group flex items-center gap-2 rounded-xl border px-3 py-2 shadow-sm transition ${s.ring} ${
				data.dimmed ? "opacity-35" : ""
			} ${data.matched ? "ring-2 ring-amber-400 ring-offset-1" : ""}`}
			style={{ width: 226 }}
		>
			<Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
			<Handle type="source" position={Position.Right} style={{ opacity: 0 }} />

			<span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${data.kind === "root" ? "bg-white/15" : "bg-slate-50"}`}>
				<Icon className={`h-4 w-4 ${s.icon}`} />
			</span>

			<button type="button" onClick={data.onOpen} className="min-w-0 flex-1 text-left" title={data.sublabel || data.label}>
				<div className="truncate text-[13px] font-semibold leading-tight">{data.label}</div>
				{data.sublabel && <div className={`truncate text-[10px] ${data.kind === "root" ? "text-white/70" : "text-slate-400"}`}>{data.sublabel}</div>}
			</button>

			{data.kind === "dynamic" && typeof data.recordCount === "number" && (
				<span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${s.chip}`}>
					<FiDatabase className="h-2.5 w-2.5" /> {data.recordCount.toLocaleString("fr-FR")}
				</span>
			)}

			{data.hasChildren && (
				<button
					type="button"
					onClick={data.onToggle}
					className={`flex h-6 shrink-0 items-center gap-1 rounded-md px-1.5 text-[10px] font-bold ${s.chip} hover:brightness-95`}
					title={data.expanded ? "Réduire" : `Développer ${data.childCount}`}
				>
					{data.childCount}
					{data.expanded ? <FiChevronDown className="h-3 w-3" /> : <FiChevronRight className="h-3 w-3" />}
				</button>
			)}
		</div>
	);
};

export const architectureNodeTypes = { arch: ArchitectureNode };
export default ArchitectureNode;
